
import { Link } from "react-router-dom";
import { blogPosts } from "@/data/blogPosts";
import { CATEGORY_ICONS } from "@/utils/image/constants";
import { Clock, Briefcase, DollarSign, Megaphone, Code, Palette, FileText } from "lucide-react";

interface RelatedPostsProps {
  currentPostId: string;
  category: string;
}

interface RelatedPostCardProps {
  slug: string;
  title: string;
  publishedAt: string;
  category: string;
}

function RelatedPostCard({ slug, title, publishedAt, category }: RelatedPostCardProps) {
  // Get appropriate icon for this category
  const categoryIcon = CATEGORY_ICONS[category] || CATEGORY_ICONS['default'];
  
  return (
    <Link 
      key={slug} 
      to={`/blog/${slug}`} 
      className="group block bg-card hover:shadow-md transition-all duration-300 border border-border/40 rounded-lg overflow-hidden"
    >
      <div className="h-40 overflow-hidden bg-muted/30 flex items-center justify-center">
        <div className="text-primary opacity-75 transform group-hover:scale-110 transition-transform duration-500">
          {/* Render dynamic icon based on category */}
          {categoryIcon === 'clock' && <Clock size={36} />}
          {categoryIcon === 'briefcase' && <Briefcase size={36} />}
          {categoryIcon === 'dollar-sign' && <DollarSign size={36} />}
          {categoryIcon === 'megaphone' && <Megaphone size={36} />}
          {categoryIcon === 'code' && <Code size={36} />}
          {categoryIcon === 'palette' && <Palette size={36} />}
          {categoryIcon === 'file-text' && <FileText size={36} />}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-medium line-clamp-2 group-hover:text-primary transition-colors">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {new Date(publishedAt).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}
        </p>
      </div>
    </Link>
  );
}

export function RelatedPosts({ currentPostId, category }: RelatedPostsProps) {
  // Filter posts from the same category, excluding current post
  const relatedPosts = blogPosts
    .filter(post => post.id !== currentPostId && post.category === category)
    .slice(0, 3);
  
  if (relatedPosts.length === 0) {
    return null;
  }
  
  return (
    <div className="mt-8">
      <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
        {relatedPosts.map(post => (
          <RelatedPostCard 
            key={post.id}
            slug={post.slug}
            title={post.title}
            publishedAt={post.publishedAt}
            category={post.category}
          />
        ))}
      </div>
    </div>
  );
}
